/**
 * Meta (Facebook) Pixel — Vitararx checkout
 * Pixel ID is initialized once; events use the fbq queue until the script loads.
 */

export const META_PIXEL_ID = "2475243879590131";

const STORAGE_PURCHASE_PREFIX = "meta_fb_purchase_fired_";

/**
 * ISO 4217 from shipping country (extend as you add markets).
 * @param {string} country
 * @returns {string}
 */
export function resolveEcommerceCurrency(country) {
  const c = String(country || "")
    .trim()
    .toLowerCase();
  if (c === "canada" || c === "ca") return "CAD";
  return "USD";
}

function installFbqStub() {
  if (typeof window === "undefined" || window.fbq) return;
  const f = window;
  const b = document;
  const e = "script";
  const v = "https://connect.facebook.net/en_US/fbevents.js";
  let n, t, s;
  if (f.fbq) return;
  n = f.fbq = function () {
    n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
  };
  if (!f._fbq) f._fbq = n;
  n.push = n;
  n.loaded = !0;
  n.version = "2.0";
  n.queue = [];
  t = b.createElement(e);
  t.async = !0;
  t.src = v;
  s = b.getElementsByTagName(e)[0];
  s.parentNode.insertBefore(t, s);
}

function ensureInit() {
  if (typeof window === "undefined") return;
  installFbqStub();
  if (!window._metaPixelInited) {
    window.fbq("init", META_PIXEL_ID);
    window._metaPixelInited = true;
  }
}

function parseMoney(value) {
  if (value === null || value === undefined || value === "") return 0;
  const n =
    typeof value === "string" ? parseFloat(value.replace(/,/g, "")) : Number(value);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Build `contents` / `content_ids` / `num_items` from checkout product rows.
 * @param {Array<{ product_id?: string, product_price?: string|number }>} products
 */
export function buildMetaProductPayload(products) {
  if (!products || !Array.isArray(products) || products.length === 0) {
    return { contents: [], content_ids: [], num_items: 0 };
  }
  const contents = products.map((p) => ({
    id: String(p.product_id || ""),
    quantity: 1,
    item_price: parseMoney(p.product_price),
  }));
  const content_ids = contents.map((c) => c.id).filter(Boolean);
  return { contents, content_ids, num_items: contents.length };
}

export function metaPageView() {
  if (typeof window === "undefined") return;
  const path = window.location?.pathname || "";
  // If the pixel snippet already fired for this exact path, avoid duplicates.
  if (window._metaFbPageViewPath && window._metaFbPageViewPath === path) return;
  ensureInit();
  window._metaFbPageViewPath = path;
  window.fbq("track", "PageView");
}

export function metaTrackViewContent({ products, value, currency = "USD" }) {
  if (typeof window === "undefined") return;
  ensureInit();
  const { contents, content_ids, num_items } = buildMetaProductPayload(products);
  const v = parseMoney(value);
  window.fbq("track", "ViewContent", {
    content_type: "product",
    content_ids: content_ids.length ? content_ids : undefined,
    contents: contents.length ? contents : undefined,
    num_items: num_items || undefined,
    value: v > 0 ? v : undefined,
    currency,
  });
}

export function metaTrackInitiateCheckout({ products, value, currency = "USD" }) {
  if (typeof window === "undefined") return;
  ensureInit();
  const { contents, content_ids, num_items } = buildMetaProductPayload(products);
  const v = parseMoney(value);
  window.fbq("track", "InitiateCheckout", {
    content_type: "product",
    content_ids: content_ids.length ? content_ids : undefined,
    contents: contents.length ? contents : undefined,
    num_items: num_items || undefined,
    value: v > 0 ? v : undefined,
    currency,
  });
}

export function metaTrackAddPaymentInfo({ products, value, currency = "USD" }) {
  if (typeof window === "undefined") return;
  ensureInit();
  const { contents, content_ids, num_items } = buildMetaProductPayload(products);
  const v = parseMoney(value);
  window.fbq("track", "AddPaymentInfo", {
    content_type: "product",
    content_ids: content_ids.length ? content_ids : undefined,
    contents: contents.length ? contents : undefined,
    num_items: num_items || undefined,
    value: v > 0 ? v : undefined,
    currency,
  });
}

/**
 * Purchase — call only after confirmed order. Use {@link hasMetaPurchaseBeenFired} / {@link markMetaPurchaseFired} to dedupe refreshes.
 * @param {{ value: number|string, currency: string, orderId: string, products?: Array, coupon?: string }} params
 */
export function metaTrackPurchase({ value, currency, orderId, products, coupon }) {
  if (typeof window === "undefined") return;
  ensureInit();
  const { contents, content_ids, num_items } = buildMetaProductPayload(products || []);
  const v = parseMoney(value);
  const payload = {
    value: v,
    currency: currency || "USD",
    content_type: "product",
    content_ids: content_ids.length ? content_ids : undefined,
    contents: contents.length ? contents : undefined,
    num_items: num_items || undefined,
  };
  if (coupon) payload.coupon = String(coupon);
  window.fbq("track", "Purchase", payload, { eventID: `purchase_${orderId}` });
}

export function metaPurchaseStorageKey(orderId) {
  return `${STORAGE_PURCHASE_PREFIX}${orderId}`;
}

export function hasMetaPurchaseBeenFired(orderId) {
  if (typeof window === "undefined" || !orderId) return false;
  try {
    return sessionStorage.getItem(metaPurchaseStorageKey(orderId)) === "1";
  } catch {
    return false;
  }
}

export function markMetaPurchaseFired(orderId) {
  if (typeof window === "undefined" || !orderId) return;
  try {
    sessionStorage.setItem(metaPurchaseStorageKey(orderId), "1");
  } catch {
    /* private mode / quota */
  }
}
